import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useLocalizedPath } from "../components/Header";
import {
  LayoutGrid,
  CheckCircle,
  ArrowLeft,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
  ClipboardList,
  AlertTriangle,
} from "lucide-react";

export default function TrockenbauDetail() {
  const { t } = useTranslation();
  const { getPath } = useLocalizedPath();

  const workSteps = t('drywallDetail.workMethod.steps', { returnObjects: true });
  const servicesOffered = t('drywallDetail.scope.offered.items', { returnObjects: true });

  return (
    <div data-testid="drywall-detail-page">
      {/* Hero Section */}
      <section className="bg-[#2D3748] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <Link
              to={getPath('services')}
              className="inline-flex items-center text-gray-300 hover:text-white mb-6 transition-colors"
              data-testid="back-to-services"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('drywallDetail.backToServices')}
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-3 rounded-xl bg-orange-500 text-white">
                <LayoutGrid className="h-8 w-8" />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-white">
                {t('drywallDetail.hero.title')}
              </h1>
            </div>
            <p className="text-xl text-gray-300 leading-relaxed mb-4">
              {t('drywallDetail.hero.subtitle')}
            </p>
            <p className="text-lg text-gray-400 leading-relaxed">
              {t('drywallDetail.hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Work Method Section */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 rounded-lg bg-orange-100 text-orange-500">
                  <ClipboardList className="h-6 w-6" />
                </div>
                <h2 className="text-3xl font-bold font-['Manrope'] text-[#2D3748]">
                  {t('drywallDetail.workMethod.title')}
                </h2>
              </div>
              <p className="text-gray-600 leading-relaxed mb-6">
                {t('drywallDetail.workMethod.intro')}
              </p>
              <p className="text-gray-600 leading-relaxed mb-4">
                {t('drywallDetail.workMethod.stepsIntro')}
              </p>
            </div>

            <Card className="bg-gray-50 border-0">
              <CardContent className="p-6 md:p-8">
                <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748] mb-6">
                  {t('drywallDetail.workMethod.stepsTitle')}
                </h3>
                <ul className="space-y-4">
                  {Array.isArray(workSteps) && workSteps.map((step, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12">
            <Card className="border border-gray-200">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <ShieldCheck className="h-6 w-6 text-orange-500" />
                  <h3 className="text-lg font-semibold font-['Manrope'] text-[#2D3748]">
                    {t('drywallDetail.workMethod.cleanTitle')}
                  </h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  {t('drywallDetail.workMethod.clean')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Scope of Services */}
      <section className="py-16 md:py-20 bg-[#F7FAFC]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-[#2D3748] mb-4">
              {t('drywallDetail.scope.title')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Services Offered */}
            <Card className="bg-white border-0 shadow-lg">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-green-100 text-green-600">
                    <CheckCircle className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748]">
                    {t('drywallDetail.scope.offered.title')}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {Array.isArray(servicesOffered) && servicesOffered.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Reserved Work Notice */}
            <Card className="bg-amber-50 border border-amber-200">
              <CardContent className="p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 rounded-lg bg-amber-100 text-amber-600">
                    <AlertTriangle className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-semibold font-['Manrope'] text-[#2D3748]">
                    {t('drywallDetail.scope.reserved.title')}
                  </h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {t('drywallDetail.scope.reserved.description')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-24 bg-[#2D3748]" data-testid="drywall-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Manrope'] text-white mb-4">
              {t('drywallDetail.contact.title')}
            </h2>
            <p className="text-lg text-gray-300 mb-4 leading-relaxed">
              {t('drywallDetail.contact.description1')}
            </p>
            <p className="text-lg text-gray-300 mb-8 leading-relaxed">
              {t('drywallDetail.contact.description2')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-lg h-14 px-8"
                data-testid="drywall-cta-email"
              >
                <a href="mailto:info@mtrenovierung.de?subject=Anfrage%20Trockenbau">
                  <Mail className="mr-2 h-5 w-5" />
                  {t('drywallDetail.contact.requestQuote')}
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-[#2D3748] text-lg h-14 px-8 bg-transparent"
                data-testid="drywall-cta-phone"
              >
                <a href="tel:+4915207684235">
                  <Phone className="mr-2 h-5 w-5" />
                  {t('common.callNow')}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
